/**
 * ConnectionSearch
 *
 * Renders saved connections as command palette items.
 * Typing "Connect" shows all connections; typing a connection name
 * shows matching ones. Each connection can be quick-connected (Enter)
 * or edited (secondary button).
 */

import { Pencil, Server, PlusCircle } from 'lucide-react';
import { CommandGroup, CommandItem } from '@/components/ui/command';
import { toast } from '@/components/ui/toaster';
import { useConnectionStore } from '../../connections/store/connectionStore';
import { useConnectionsUIStore } from '../../connections/store/uiStore';
import { useCommandPaletteStore } from '../store/commandPaletteStore';
import type { Connection } from '../../../types/electron';
import { safeConnectionName, safeConnectionServer } from '@/types/safeConnectionDisplay';

/** Close palette and run after a tick (prevents dialog conflicts) */
function closeAndRun(fn: () => void) {
  useCommandPaletteStore.getState().close();
  setTimeout(fn, 0);
}

function getDisplayName(conn: Connection): string {
  return safeConnectionName(conn);
}

function getSubtitle(conn: Connection): string {
  const parts: string[] = [];
  if (conn.alias) parts.push(safeConnectionServer(conn));
  if (conn.engine && conn.engine !== 'mssql') parts.push(conn.engine.toUpperCase());
  return parts.join(' · ');
}

export function ConnectionSearch() {
  const connections = useConnectionStore((s) => s.connections);
  const activeConnections = useConnectionStore((s) => s.activeConnections);

  if (connections.length === 0) return null;

  const connectedIds = new Set(
    activeConnections.filter((c) => c.status === 'connected').map((c) => c.id)
  );

  const handleConnect = (conn: Connection) => {
    const name = getDisplayName(conn);
    const alreadyConnected = connectedIds.has(conn.id);
    window.electronAPI?.analytics
      ?.track('command-palette:connection_connect', { alreadyConnected })
      .catch(() => {});
    closeAndRun(async () => {
      if (alreadyConnected) {
        toast.default({
          title: `Already connected to ${name}`,
          testId: 'palette-already-connected-toast',
        });
        return;
      }
      // The palette closes before the dial settles, so whichever screen is
      // behind may have no visible connecting state (the explorer tree has no
      // node yet; the start-page card can be folded away). The toast is the
      // one surface guaranteed to be seen — persist while dialing, then
      // resolve to the outcome.
      const toastId = toast.default({
        title: `Connecting to ${name}…`,
        persist: true,
        testId: 'palette-connecting-toast',
      });
      const success = await useConnectionStore.getState().connectToDatabase(conn);
      toast.dismiss(toastId);
      if (success) {
        toast.success({ title: `Connected to ${name}`, testId: 'palette-connected-toast' });
      } else {
        const error = useConnectionStore.getState().connectionErrorById[conn.id];
        // No stored error means the user cancelled (password prompt / abandon)
        // — stay quiet, exactly like the connection cards do.
        if (error) {
          toast.error({
            title: `Failed to connect to ${name}`,
            description: error,
            testId: 'palette-connect-failed-toast',
          });
        }
      }
    });
  };

  const handleEdit = (e: React.MouseEvent, conn: Connection) => {
    e.stopPropagation();
    e.preventDefault();
    window.electronAPI?.analytics?.track('command-palette:connection_edit', {}).catch(() => {});
    closeAndRun(() => {
      // The palette can float over the connections dialogs (window-level
      // shortcut). App.tsx suppresses the direct edit host while either is
      // open, so close them first — the user asked to edit THIS connection,
      // not to keep the list dialog underneath.
      const ui = useConnectionsUIStore.getState();
      ui.setShowConnectionsDialog(false);
      ui.setShowNewConnectionDialog(false);
      ui.setEditingConnection(conn);
    });
  };

  const handleNewConnection = () => {
    closeAndRun(() => {
      useConnectionsUIStore.getState().setShowNewConnectionDialog(true);
    });
  };

  return (
    <CommandGroup heading="Connections">
      {connections.map((conn) => {
        const isConnected = connectedIds.has(conn.id);
        const name = getDisplayName(conn);
        const subtitle = getSubtitle(conn);

        return (
          <CommandItem
            key={`connect:${conn.id}`}
            value={`connect ${name} ${safeConnectionServer(conn)}`}
            keywords={['connect', name, safeConnectionServer(conn), conn.engine || '']}
            onSelect={() => handleConnect(conn)}
            data-testid={`connection-search-${conn.id}`}
          >
            <Server className="mr-2 h-4 w-4 shrink-0 opacity-70" />
            <span className="flex-1 min-w-0 flex items-center gap-2">
              <span className="truncate">{name}</span>
              {isConnected && (
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-500 shrink-0" title="Connected" />
              )}
              {subtitle && (
                <span className="text-xs text-muted-foreground/60 truncate">{subtitle}</span>
              )}
            </span>
            <button
              type="button"
              className="ml-1 p-0.5 rounded hover:bg-muted-foreground/20 text-muted-foreground/50 hover:text-muted-foreground transition-colors"
              title="Edit connection"
              onClick={(e) => handleEdit(e, conn)}
              onPointerDown={(e) => e.stopPropagation()}
              data-testid={`connection-edit-${conn.id}`}
            >
              <Pencil className="h-3 w-3" />
            </button>
          </CommandItem>
        );
      })}

      {/* "New Connection" item — shown when searching for "connect" */}
      <CommandItem
        key="connect:new"
        value="connect new connection add"
        keywords={['connect', 'new', 'add', 'connection']}
        onSelect={handleNewConnection}
        data-testid="connection-search-new"
      >
        <PlusCircle className="mr-2 h-4 w-4 shrink-0 opacity-70" />
        <span className="flex-1">New Connection</span>
      </CommandItem>
    </CommandGroup>
  );
}
